import { CommonModule, NgClass } from '@angular/common';
import {
  Component,
  Input,
  EventEmitter,
  OnInit,
  OnDestroy,
  HostListener,
} from '@angular/core';
import {
  NbCalendarCell,
  NbTooltipModule,
  NbDateService,
  NbCalendarRange,
} from '@nebular/theme';
import { Subscription } from 'rxjs';
import { AttendanceService } from './attendance.service';

@Component({
  selector: 'app-leave-day-cell',
  standalone: true,
  imports: [NgClass, NbTooltipModule, CommonModule],
  template: `
    <div
      class="leave-cell-content"
      [ngClass]="statusClass"
      [class.today-cell]="isTodayCell"
      (click)="onClick()"
      [nbTooltip]="tooltipText"
      [nbTooltipDisabled]="!tooltipText"
    >
      {{ date.getDate() }}
    </div>
  `,
  styleUrls: ['./leave-day-cell.component.scss'],
})
export class LeaveDayCellComponent
  implements NbCalendarCell<Date, any>, OnInit, OnDestroy {

  @Input() date!: Date;
  @Input() data: any;
  @Input() select = new EventEmitter<Date>();
  @Input() tooltipText: string = '';

  isRangeStart = false;
  isRangeEnd = false;
  isInRange = false;

  private subscription = new Subscription();
  private currentRange: NbCalendarRange<Date> | null = null;
  private hoverDate: Date | null = null;

  constructor(
    protected dateService: NbDateService<Date>,
    private attendanceService: AttendanceService
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.attendanceService.leaveRange$.subscribe(range => {
        this.currentRange = range;
        this.updateRangeStatus();
      })
    );

    this.subscription.add(
      this.attendanceService.hoverDate$.subscribe(date => {
        this.hoverDate = date;
        this.updateRangeStatus();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onClick(): void {
    this.select.emit(this.date);
  }

  // 🔥 Hover Preview
  @HostListener('mouseenter')
  onMouseEnter() {
    if (this.currentRange?.start && !this.currentRange?.end) {
      this.attendanceService.setHoverDate(this.date);
    }
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    if (!this.currentRange?.end) {
      this.attendanceService.setHoverDate(null);
    }
  }

  private updateRangeStatus(): void {

    if (!this.currentRange?.start) {
      this.reset();
      return;
    }

    const start = this.currentRange.start;
    const end = this.currentRange.end ?? this.hoverDate;

    if (!end) {
      this.isRangeStart = this.dateService.isSameDay(this.date, start);
      this.isRangeEnd = false;
      this.isInRange = false;
      return;
    }

    const realStart = start < end ? start : end;
    const realEnd = start < end ? end : start;

    this.isRangeStart = this.dateService.isSameDay(this.date, realStart);
    this.isRangeEnd = this.dateService.isSameDay(this.date, realEnd);

    this.isInRange =
      !this.isRangeStart &&
      !this.isRangeEnd &&
      this.date > realStart &&
      this.date < realEnd;
  }

  private reset() {
    this.isRangeStart = false;
    this.isRangeEnd = false;
    this.isInRange = false;
  }

  get statusClass(): string {
    const classes = [];

    if (this.data) {
      classes.push(
        this.data.status?.toLowerCase()?.replace(/\s+/g, '-') || ''
      );
    }

    if (this.isRangeStart) classes.push('range-start');
    if (this.isRangeEnd) classes.push('range-end');
    if (this.isInRange) classes.push('in-range');

    return classes.join(' ');
  }

  get isTodayCell(): boolean {
    const today = this.dateService.today();
    return (
      this.date.getFullYear() === today.getFullYear() &&
      this.date.getMonth() === today.getMonth() &&
      this.date.getDate() === today.getDate()
    );
  }
}
